import type { BackendQuest } from '@/lib/adapters';

/**
 * Visitor preview of the quest catalogue.
 *
 * `/v1/quests` is auth-gated (the backend has no public catalogue endpoint), so
 * disconnected clients can't be served the live list. These entries mirror the
 * backend catalogue in its **wire shape** (`BackendQuest`) — same `code`, `type`
 * and `config` the API returns — and are run through the exact same `adaptQuest`
 * pipeline as the authed list (see `lib/api.ts`). Keeping the raw backend shape
 * here (rather than a hand-mapped frontend `Quest`) means the preview can only
 * differ from a logged-in view by the data itself, never by the mapping.
 *
 * `useQuests` forces every card to `locked` for visitors so they can browse but
 * not claim. Any type the adapter still marks `coming_soon` is preserved as-is
 * (none today — every backend verifier has shipped).
 *
 * Keep the `type`/`points`/`order_index` aligned with the admin catalogue
 * (managed from /v1/admin/quests) whenever it changes.
 */
export const PUBLIC_QUESTS: BackendQuest[] = [
  {
    id: 'visit_homepage',
    code: 'visit_homepage',
    type: 'visit_link',
    title: 'Visit our website',
    description: 'Step into the BigWater universe and mark your first move.',
    points: 10,
    order_index: 10,
    config: { url: 'https://bigwater.io' },
    completed: false,
  },
  {
    id: 'join_telegram',
    code: 'join_telegram',
    type: 'tg_join',
    title: 'Join our Telegram community',
    description: 'Get into the fastest community channel and catch the early drops.',
    points: 50,
    order_index: 20,
    config: { invite_url: 'https://t.me/+bMt2BypA8TA4ODBl' },
    completed: false,
  },
  {
    id: 'join_discord',
    code: 'join_discord',
    type: 'discord_join',
    title: 'Join our Discord server',
    description: 'Enter the coordination hub where the next wave gets organized.',
    points: 50,
    order_index: 30,
    config: { invite_url: 'https://discord.gg/PYTbTT7Bkt' },
    completed: false,
  },
  {
    id: 'follow_x',
    code: 'follow_x',
    type: 'x_follow',
    title: 'Follow us on X',
    description: 'Track the public signal and stay close to campaign drops.',
    points: 50,
    order_index: 40,
    config: { target_handle: 'BigWProtocol' },
    completed: false,
  },
  {
    id: 'like_launch_post',
    code: 'like_launch_post',
    type: 'x_like',
    title: 'Like our launch post',
    description: 'Boost the launch signal and put your wallet on the board.',
    points: 30,
    order_index: 50,
    config: {},
    completed: false,
  },
  {
    id: 'retweet_launch_post',
    code: 'retweet_launch_post',
    type: 'x_retweet',
    title: 'Retweet our launch post',
    description: 'Carry the launch signal further and climb with the first wave.',
    points: 40,
    order_index: 60,
    config: {},
    completed: false,
  },
];
